import React, { useState, useEffect, useCallback } from 'react';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';
import api from '../components/api';
import { useToast } from '../components/ToastContext';
import '../css/SchilderplanningBalkons.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  te_plannen: 'Te plannen',
  gepland: 'Gepland',
  bezig: 'Bezig',
  afgerond: 'Afgerond',
  beoordeeld: 'Beoordeeld'
};

const fileIdFromUrl = (url) => {
  if (!url) return null;
  const m = String(url).match(/\/d\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
};
const photoSrc = (foto) => {
  const id = foto.driveFileId || fileIdFromUrl(foto.driveUrl);
  return id ? `${API_BASE}/api/inspections/photo/${id}` : foto.driveUrl;
};

const urgentieKleur = (u) => {
  if (u >= 60) return '#c0392b';
  if (u >= 35) return '#e67e22';
  if (u >= 15) return '#f1c40f';
  return '#27ae60';
};

const SchilderplanningBalkons = () => {
  const toast = useToast();
  const [tab, setTab] = useState('lijst'); // lijst | uren
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({}); // sessionId -> balkonfoto's
  const [maandPerHuis, setMaandPerHuis] = useState({});
  const [busy, setBusy] = useState({}); // huisnummer -> 'ai' | 'invite'
  const [selectie, setSelectie] = useState({}); // huisnummer -> bool (nog uit te nodigen)

  // Uren/vakantie inzicht
  const [uren, setUren] = useState(null);
  const [afwezigheid, setAfwezigheid] = useState([]);

  const fetchOverzicht = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/schilderwerk/admin/overzicht');
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Kan overzicht niet laden');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchInzicht = useCallback(async () => {
    try {
      const [u, a] = await Promise.all([
        api.get('/api/schilderwerk/admin/uren'),
        api.get('/api/schilderwerk/admin/afwezigheid')
      ]);
      setUren(u.data);
      setAfwezigheid(a.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchOverzicht(); }, [fetchOverzicht]);
  useEffect(() => { if (tab === 'uren') fetchInzicht(); }, [tab, fetchInzicht]);

  const toggleExpand = async (w) => {
    const open = !expanded[w.huisnummer];
    setExpanded(prev => ({ ...prev, [w.huisnummer]: open }));
    if (open && w.sessionId && !details[w.sessionId]) {
      try {
        const res = await api.get(`/api/inspections/admin/sessions/${w.sessionId}`);
        const s = res.data;
        const fotos = []
          .concat((s.balkonKlein && s.balkonKlein.fotos) || [])
          .concat((s.balkonGroot && s.balkonGroot.fotos) || [])
          .filter(f => f && f.uploaded && (f.driveFileId || f.driveUrl));
        setDetails(prev => ({ ...prev, [w.sessionId]: fotos }));
      } catch (err) { /* stil */ }
    }
  };

  const planMaand = (w) => maandPerHuis[w.huisnummer] || (w.klus && w.klus.planningsmaand) || (data && data.standaardPlanningsmaand) || '';

  const uitnodigen = async (w) => {
    setBusy(prev => ({ ...prev, [w.huisnummer]: 'invite' }));
    try {
      await api.post('/api/schilderwerk/admin/uitnodigen', { huisnummer: w.huisnummer, planningsmaand: planMaand(w) });
      toast.success(`Uitnodiging verstuurd naar ${w.huisnummer}`);
      fetchOverzicht();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Uitnodigen mislukt');
    } finally {
      setBusy(prev => ({ ...prev, [w.huisnummer]: null }));
    }
  };

  const aiAdvies = async (w) => {
    setBusy(prev => ({ ...prev, [w.huisnummer]: 'ai' }));
    try {
      await api.post(`/api/schilderwerk/admin/ai-advies/${w.huisnummer}`);
      toast.success(`AI-beoordeling klaar voor ${w.huisnummer}`);
      fetchOverzicht();
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI-advies mislukt');
    } finally {
      setBusy(prev => ({ ...prev, [w.huisnummer]: null }));
    }
  };

  // Direct op de schilderlijst zetten, zónder opnamestaat. Ruim de helft van de
  // woningen vult er geen in; die moeten toch ingepland kunnen worden.
  const zetOpSchilderlijst = async () => {
    const gekozen = (data.nogUitTeNodigen || []).filter(b => selectie[b.huisnummer]);
    if (gekozen.length === 0) { toast.error('Selecteer ten minste één woning'); return; }
    const maand = (data && data.standaardPlanningsmaand) || '';
    let gelukt = 0;
    for (const b of gekozen) {
      try {
        await api.post('/api/schilderwerk/admin/uitnodigen', { huisnummer: b.huisnummer, planningsmaand: maand, stuurMail: false });
        gelukt++;
      } catch (err) {
        toast.error(`${b.huisnummer}: ${err.response?.data?.message || 'uitnodigen mislukt'}`);
      }
    }
    if (gelukt > 0) toast.success(`${gelukt} woning(en) op de schilderlijst gezet (nog geen mail verstuurd)`);
    setSelectie({});
    fetchOverzicht();
  };

  const nodigOpnamestaatUit = async () => {
    const gekozen = (data.nogUitTeNodigen || []).filter(b => selectie[b.huisnummer]);
    if (gekozen.length === 0) { toast.error('Selecteer ten minste één woning'); return; }
    try {
      await api.post('/api/inspections/admin/invite', {
        bewoners: gekozen.map(b => ({ huisnummer: b.huisnummer, email: b.email, naam: b.naam }))
      });
      toast.success(`${gekozen.length} uitnodiging(en) voor opnamestaat verstuurd`);
      setSelectie({});
      fetchOverzicht();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Uitnodigen mislukt');
    }
  };

  if (loading) {
    return (
      <BeheerdersportaalSidebar>
        <div className="sp-container"><p>Laden…</p></div>
      </BeheerdersportaalSidebar>
    );
  }

  const woningen = (data && data.woningen) || [];
  const zonderOpname = (data && data.zonderOpnamestaat) || [];
  const nogUit = (data && data.nogUitTeNodigen) || [];

  const renderWoning = (w) => {
    const p = w.prioriteit || {};
    const geenOpname = !!w.geenOpnamestaat;
    const u = geenOpname ? null : (p.urgentie || 0);
    return (
      <div key={w.huisnummer} className="sp-row">
        <div className="sp-row-main" onClick={() => toggleExpand(w)}>
          <div className="sp-huis">
            <span className="sp-huisnr">{w.huisnummer}</span>
            <span className="sp-naam">{w.bewoner?.naam}</span>
          </div>
          <div className="sp-urgentie">
            {u === null ? (
              <span className="sp-urg-getal sp-muted">—</span>
            ) : (
              <>
                <div className="sp-bar"><span style={{ width: `${u}%`, background: urgentieKleur(u) }} /></div>
                <span className="sp-urg-getal">{u}</span>
              </>
            )}
          </div>
          <div className="sp-labels">
            {geenOpname && <span className="sp-chip">geen opnamestaat</span>}
            {!geenOpname && p.ernstigsteVeld && p.ernstigsteScore != null && p.ernstigsteScore < 5 && <span className="sp-chip ernst">⚠ {p.ernstigsteVeld}: {p.ernstigsteScore}</span>}
            {!geenOpname && <span className="sp-chip">{p.aantalSerieus || 0} serieus</span>}
            {w.aiAdvies && <span className="sp-chip ai">AI {w.aiAdvies.urgentieklasse}/5</span>}
            {w.discrepantie && <span className="sp-chip disc" title="Cijfers en foto's spreken elkaar tegen">⚠ check</span>}
            {w.klus && <span className={`sp-chip status ${w.klus.status}`}>{STATUS_LABELS[w.klus.status]}</span>}
          </div>
          <span className="sp-caret">{expanded[w.huisnummer] ? '▲' : '▼'}</span>
        </div>

        {expanded[w.huisnummer] && (
          <div className="sp-detail">
            <div className="sp-detail-grid">
              <div>
                <h4>Cijfers (opnamestaat)</h4>
                {geenOpname ? (
                  <p className="sp-muted">Geen opnamestaat ingevuld — deze woning staat handmatig op de lijst. Zodra de bewoner de opnamestaat invult, verschijnt hij vanzelf in de ranglijst.</p>
                ) : (
                  <>
                    <p>Klein balkon: urgentie {p.perBalkon?.klein?.pNorm ?? 0} ({p.perBalkon?.klein?.beoordeeldeVelden ?? 0} velden)</p>
                    <p>Groot balkon: urgentie {p.perBalkon?.groot?.pNorm ?? 0} ({p.perBalkon?.groot?.beoordeeldeVelden ?? 0} velden)</p>
                  </>
                )}
                <p className="sp-muted">{!geenOpname && <>Cijfer-urgentie {p.pNorm} · </>}{w.bewoner?.telefoon && <a href={`tel:${w.bewoner.telefoon}`}>{w.bewoner.telefoon}</a>} {w.bewoner?.email}</p>
              </div>
              <div>
                <h4>Inzichten (Verf Advies Centrum / AI)</h4>
                {w.aiAdvies ? (
                  <>
                    <p><strong>Urgentieklasse {w.aiAdvies.urgentieklasse}/5</strong> · betrouwbaarheid {w.aiAdvies.betrouwbaarheid}</p>
                    <p>{w.aiAdvies.motivatie}</p>
                    {w.aiAdvies.aanbevolenBehandeling && <p className="sp-muted"><em>Aanbeveling: {w.aiAdvies.aanbevolenBehandeling}</em></p>}
                  </>
                ) : geenOpname ? (
                  <p className="sp-muted">Geen balkonfoto's — AI-beoordeling pas mogelijk na een opnamestaat.</p>
                ) : (
                  <p className="sp-muted">Nog geen AI-beoordeling. {w.aantalBalkonFotos} balkonfoto's beschikbaar.</p>
                )}
              </div>
            </div>

            {details[w.sessionId] && details[w.sessionId].length > 0 && (
              <div className="sp-fotos">
                {details[w.sessionId].map((f, i) => (
                  <a key={i} href={f.driveUrl} target="_blank" rel="noreferrer">
                    <img src={photoSrc(f)} alt={`balkon ${i + 1}`} loading="lazy" />
                  </a>
                ))}
              </div>
            )}

            <div className="sp-actions">
              <label className="sp-maand">
                Planning:
                <input
                  type="month"
                  value={planMaand(w)}
                  onChange={e => setMaandPerHuis(prev => ({ ...prev, [w.huisnummer]: e.target.value }))}
                />
              </label>
              <button
                className="sp-btn primary"
                disabled={busy[w.huisnummer] === 'invite'}
                onClick={() => uitnodigen(w)}
              >
                {busy[w.huisnummer] === 'invite' ? 'Versturen…' : (w.klus ? 'Opnieuw uitnodigen' : 'Uitnodigen voor schilderwerk')}
              </button>
              {!geenOpname && (
                <button
                  className="sp-btn"
                  disabled={busy[w.huisnummer] === 'ai'}
                  onClick={() => aiAdvies(w)}
                >
                  {busy[w.huisnummer] === 'ai' ? 'AI bezig…' : 'AI-beoordeling (her)berekenen'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <BeheerdersportaalSidebar>
      <div className="sp-container">
        <div className="sp-header">
          <h1>Schilderwerk balkons</h1>
          <p className="sp-sub">Gerangschikt op urgentie — slechtste schilderwerk/houtrot bovenaan</p>
        </div>

        <div className="sp-tabs">
          <button className={tab === 'lijst' ? 'active' : ''} onClick={() => setTab('lijst')}>Ranglijst</button>
          <button className={tab === 'uren' ? 'active' : ''} onClick={() => setTab('uren')}>Uren &amp; vakantie</button>
        </div>

        {tab === 'lijst' && (
          <>
            <div className="sp-list">
              {woningen.length === 0 && <p className="sp-empty">Nog geen ingevulde opnamestaten met balkondata.</p>}
              {woningen.map(renderWoning)}
            </div>

            {zonderOpname.length > 0 && (
              <>
                <h3 className="sp-sectiekop">Op de schilderlijst zonder opnamestaat ({zonderOpname.length})</h3>
                <p className="sp-sub">Handmatig ingepland — geen cijfers, dus niet te rangschikken.</p>
                <div className="sp-list">
                  {zonderOpname.map(renderWoning)}
                </div>
              </>
            )}

            <details className="sp-noguit">
              <summary>Nog uit te nodigen voor opnamestaat ({nogUit.length})</summary>
              <div className="sp-noguit-body">
                {nogUit.length === 0 ? (
                  <p className="sp-muted">Alle bewoonde woningen hebben een opnamestaat ingevuld.</p>
                ) : (
                  <>
                    <div className="sp-noguit-list">
                      {nogUit.map(b => (
                        <label key={b.huisnummer} className="sp-noguit-row">
                          <input
                            type="checkbox"
                            checked={!!selectie[b.huisnummer]}
                            onChange={e => setSelectie(prev => ({ ...prev, [b.huisnummer]: e.target.checked }))}
                          />
                          <span className="sp-huisnr">{b.huisnummer}</span>
                          <span className="sp-naam">{b.naam}</span>
                          {!b.email && <span className="sp-chip disc">geen e-mail</span>}
                          {b.opSchilderlijst && <span className="sp-chip status te_plannen">op schilderlijst</span>}
                        </label>
                      ))}
                    </div>
                    <div className="sp-noguit-acties">
                      <button className="sp-btn primary" onClick={nodigOpnamestaatUit}>Uitnodigen voor opnamestaat</button>
                      <button className="sp-btn" onClick={zetOpSchilderlijst}>Direct op schilderlijst (zonder opnamestaat)</button>
                    </div>
                  </>
                )}
              </div>
            </details>
          </>
        )}

        {tab === 'uren' && (
          <div className="sp-inzicht">
            <h3>Urenregistratie huismeester</h3>
            {uren ? (
              <>
                <p className="sp-total">Totaal geregistreerd: <strong>{uren.totaalUren} uur</strong></p>
                <table className="sp-table">
                  <thead><tr><th>Datum</th><th>Uren</th><th>Woning</th><th>Omschrijving</th><th>Status</th></tr></thead>
                  <tbody>
                    {(uren.uren || []).map(u => (
                      <tr key={u._id}>
                        <td>{u.datum}</td>
                        <td>{u.uren}</td>
                        <td>{u.huisnummer || '—'}</td>
                        <td>{u.omschrijving || ''}</td>
                        <td>{u.status}</td>
                      </tr>
                    ))}
                    {(uren.uren || []).length === 0 && <tr><td colSpan={5} className="sp-muted">Nog geen uren geregistreerd.</td></tr>}
                  </tbody>
                </table>
              </>
            ) : <p>Laden…</p>}

            <h3 style={{ marginTop: 30 }}>Vakantie &amp; ziekte (geblokkeerde uren)</h3>
            <table className="sp-table">
              <thead><tr><th>Type</th><th>Van</th><th>Tot</th><th>Uren</th><th>Status</th></tr></thead>
              <tbody>
                {afwezigheid.map(a => (
                  <tr key={a._id} className={`afw-${a.type}`}>
                    <td>{a.type}</td><td>{a.vanDatum}</td><td>{a.totDatum}</td><td>{a.urenGeblokkeerd}</td><td>{a.status}</td>
                  </tr>
                ))}
                {afwezigheid.length === 0 && <tr><td colSpan={5} className="sp-muted">Geen afwezigheid geregistreerd.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </BeheerdersportaalSidebar>
  );
};

export default SchilderplanningBalkons;
